<script setup lang="ts">
import type { z } from 'zod';
import type { historySchema, summarySchema } from '@atum-remote/nyx-link-protocol';
import { formatRelativeTime, formatTimestamp } from '../lib/format.js';
import AppIcon from './AppIcon.vue';

type Session = z.infer<typeof historySchema>['sessions'][number];
type Summary = z.infer<typeof summarySchema>;

/** A captured session: compact summary and linked work, read-only. */
defineProps<{ session: Session; summary: Summary | null; busy: boolean }>();
</script>

<template>
  <section class="nyx-session">
    <header class="nyx-conversation-head">
      <div class="nyx-head-text">
        <span class="eyebrow">Captured session</span>
        <h2>{{ session.title ?? session.sessionId }}</h2>
        <div class="nyx-head-meta">
          <span class="tag">{{ session.client }}</span>
          <span class="tag">{{ session.status }}</span>
          <span v-if="session.model" class="tag mono">{{ session.model }}</span>
          <span class="nyx-head-time" :title="formatTimestamp(session.updatedAt)">
            Updated {{ formatRelativeTime(session.updatedAt) }}
          </span>
        </div>
      </div>
    </header>
    <div class="nyx-session-body">
      <p v-if="!summary && busy" class="nyx-transcript-empty">Loading the summary…</p>
      <p v-else-if="!summary" class="nyx-transcript-empty">No summary was returned.</p>
      <article v-else class="nyx-summary">
        <p class="nyx-summary-text">
          {{ summary.summary?.text || 'No summary is available for this session.' }}
        </p>
        <div v-if="summary.summary?.goal" class="nyx-summary-block">
          <h3>Goal</h3>
          <p>{{ summary.summary.goal }}</p>
        </div>
        <div v-if="summary.summary?.nextActions.length" class="nyx-summary-block">
          <h3>Next actions</h3>
          <ul class="feature-list">
            <li v-for="item in summary.summary.nextActions" :key="item">
              <AppIcon name="chevronRight" />{{ item }}
            </li>
          </ul>
        </div>
        <div v-if="summary.summary?.blockers.length" class="nyx-summary-block">
          <h3>Blockers</h3>
          <ul class="feature-list" data-tone="warn">
            <li v-for="item in summary.summary.blockers" :key="item">
              <AppIcon name="warning" />{{ item }}
            </li>
          </ul>
        </div>
        <div v-if="summary.workItems.length" class="nyx-summary-block">
          <h3>Linked work</h3>
          <ul class="nyx-workitems">
            <li v-for="item in summary.workItems" :key="item.key">
              <span class="mono">{{ item.key }}</span>
              <span class="nyx-workitem-title">{{ item.title ?? 'Untitled' }}</span>
              <span v-if="item.status" class="tag">{{ item.status }}</span>
            </li>
          </ul>
        </div>
        <p class="hint">
          {{ summary.truncated ? 'Truncated summary.' : 'Compact summary.' }} Open Nyx Link for the
          full record.
        </p>
      </article>
    </div>
    <div class="callout">
      <AppIcon name="info" />
      <div>
        <strong>Captured history is read-only</strong>
        <p>
          Existing IDE conversations are kept as records. To continue this work, start a new hosted
          task in the same project.
        </p>
      </div>
    </div>
  </section>
</template>
